import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import { LOGIN_PATH } from '../components/LegalLinks';
import { canAccess, canAccessAny } from '../permissions/canAccess';
import { getDefaultLandingRoute } from '../permissions/landingRoute';
import { useLogout, useRetryPermissions } from '../hooks/useAuth';
import { usePermissionState } from '../stores/permissionStore';

export function PermissionsLoadFailure() {
    const retryPermissions = useRetryPermissions();
    const logout = useLogout();

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={2}
            p={3}
        >
            <Typography variant="h6">Unable to load permissions</Typography>
            <Typography color="text.secondary">
                Your session is active, but we could not load portal access. Retry or sign out.
            </Typography>
            <Box display="flex" gap={1}>
                <Button variant="contained" onClick={() => void retryPermissions()}>
                    Retry
                </Button>
                <Button variant="outlined" onClick={() => logout()}>
                    Sign out
                </Button>
            </Box>
        </Box>
    );
}

function PermissionGuard({ allowed, children }) {
    const permissionState = usePermissionState();
    const userCompany = useSelector((state) => state.CompanyReducer.userCompanies);
    const authenticated = useSelector((state) => state.UserReducer.isAuthenticated);

    if (!authenticated) {
        return <Navigate to={LOGIN_PATH} />;
    }

    if (permissionState.error) {
        return <PermissionsLoadFailure />;
    }

    if (!permissionState.loaded) {
        return null;
    }

    if (!allowed(permissionState, userCompany)) {
        const landingRoute = getDefaultLandingRoute(permissionState, userCompany) ?? '/account';
        return <Navigate to={landingRoute} replace />;
    }

    return children;
}

export function RequireAppView({ appViewId, children }) {
    return (
        <PermissionGuard allowed={(permissionState) => canAccess(appViewId, permissionState)}>
            {children}
        </PermissionGuard>
    );
}

export function RequireAnyAppView({ appViewIds, children }) {
    return (
        <PermissionGuard allowed={(permissionState) => canAccessAny(appViewIds, permissionState)}>
            {children}
        </PermissionGuard>
    );
}
